/**
 * Canonical Clerk unsafeMetadata keys for membership profile:
 *   alias, telegram, birthDate (camelCase)
 *
 * Legacy portal sign-up used telegram_alias + birth_date — readers fall back
 * to those keys; writers emit canonical keys only.
 */

export type MembershipProfileFields = {
  alias: string;
  telegram: string;
  birthDate: string;
};

export function toIsoBirthDate(ymdOrIso: string): string {
  const raw = (ymdOrIso ?? "").trim();
  if (!raw) return "";
  if (raw.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return new Date(`${raw}T00:00:00.000Z`).toISOString();
  }
  return raw;
}

function aliasFromTelegram(telegram: string): string {
  const bare = telegram.replace(/^@+/, "").trim();
  return bare.length >= 3 ? bare.slice(0, 30) : "";
}

/** Read profile fields from Clerk unsafeMetadata (canonical + legacy fallback). */
export function readMembershipProfileFromMeta(
  meta: Record<string, unknown> | null | undefined,
): MembershipProfileFields {
  const m = meta ?? {};

  const telegram =
    (typeof m.telegram === "string" ? m.telegram.trim() : "") ||
    (typeof m.telegram_alias === "string" ? m.telegram_alias.trim() : "");

  const birthDate =
    (typeof m.birthDate === "string" ? m.birthDate.trim() : "") ||
    (typeof m.birth_date === "string" ? m.birth_date.trim() : "");

  let alias = typeof m.alias === "string" ? m.alias.trim() : "";
  if (alias.length < 3 && telegram) {
    alias = aliasFromTelegram(telegram);
  }

  return { alias, telegram, birthDate };
}

export function hasMembershipTelegram(
  meta: Record<string, unknown> | null | undefined,
): boolean {
  return readMembershipProfileFromMeta(meta).telegram.length >= 3;
}

/** Derive display alias when the portal form has no dedicated alias field. */
export function deriveMembershipAlias(input: {
  firstName?: string;
  lastName?: string;
  telegram?: string;
  email?: string;
}): string {
  const first = (input.firstName ?? "").trim();
  const last = (input.lastName ?? "").trim();
  const full = [first, last].filter(Boolean).join(" ").trim();
  if (full.length >= 3) return full.slice(0, 30);

  const fromTelegram = aliasFromTelegram(input.telegram ?? "");
  if (fromTelegram.length >= 3) return fromTelegram;

  const emailLocal = (input.email ?? "").split("@")[0]?.trim() ?? "";
  if (emailLocal.length >= 3) return emailLocal.slice(0, 30);

  return "";
}

/** Build canonical unsafeMetadata profile slice (merges optional extras). */
export function buildMembershipProfileMetadata(
  profile: MembershipProfileFields,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...(extra ?? {}),
    alias: profile.alias.trim(),
    telegram: profile.telegram.trim(),
    birthDate: toIsoBirthDate(profile.birthDate),
  };
}

/** Admin/API shape: DB columns stay snake_case; values from canonical or legacy meta. */
export function readMembershipProfileForAdmin(
  meta: Record<string, unknown> | null | undefined,
): { telegram_alias: string | null; birth_date: string | null; alias: string | null } {
  const { alias, telegram, birthDate } = readMembershipProfileFromMeta(meta);
  const birthYmd =
    birthDate.length >= 10
      ? birthDate.slice(0, 10)
      : birthDate || null;
  return {
    alias: alias || null,
    telegram_alias: telegram || null,
    birth_date: birthYmd,
  };
}